import { createGlobalStyle } from 'styled-components';

// Soft pastel color palette
export const theme = {
  colors: {
    primary: '#f7ad79', // Primary
    primaryLight: '#EFC8AD', // Primary light
    secondary: '#e48b88', // Coral pink
    tertiary: '#bb7693', // Purple-pink
    accent: '#85698f', // Medium purple
    darkAccent: '#515a7a', // Dark blue-purple
    deepDark: '#2f4858', // Deep blue-gray
    background: '#F8F9FA', // Soft cream background
    text: '#2f4858', // Deep blue-gray for text
    lightText: '#515a7a', // Dark blue-purple for secondary text
    error: '#e48b88', // Coral pink for errors
    success: '#85698f', // Medium purple for success
    white: '#FFFFFF',
    black: '#333333',
  },
  fonts: {
    main: "'Montserrat Alternates', sans-serif",
    heading: "'Montserrat', sans-serif",
    accent: "'Quicksand', sans-serif",
    custom: "'CustomFont', sans-serif", // For when a local font is added
  },
  breakpoints: {
    mobile: '576px',
    tablet: '768px',
    desktop: '1024px',
    large: '1200px',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
  },
  borderRadius: {
    small: '4px',
    medium: '8px',
    large: '12px',
    round: '50%',
  },
  shadows: {
    small: '0 2px 8px rgba(0, 0, 0, 0.1)',
    medium: '0 4px 12px rgba(0, 0, 0, 0.1)',
    large: '0 8px 16px rgba(0, 0, 0, 0.1)',
  },
  // Mobile-specific values
  mobile: {
    minTouchTarget: '44px', // Apple's recommended minimum touch target
    headerHeight: '60px',
    bottomNavHeight: '70px',
    safeAreaTop: 'env(safe-area-inset-top)',
    safeAreaBottom: 'env(safe-area-inset-bottom)',
    safeAreaLeft: 'env(safe-area-inset-left)',
    safeAreaRight: 'env(safe-area-inset-right)',
    gradientBg: 'linear-gradient(135deg, #f7ad79 0%, #e48b88 25%, #bb7693 50%, #85698f 75%, #515a7a 100%)',
    cardBg: 'rgba(255, 255, 255, 0.15)',
    cardBgSolid: 'rgba(255, 255, 255, 0.9)',
    overlayBg: 'rgba(47, 72, 88, 0.1)',
    shadowColor: 'rgba(47, 72, 88, 0.15)',
  }
};

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Montserrat+Alternates:wght@300;400;500;600;700&family=Montserrat:wght@400;500;600;700&family=Quicksand:wght@400;500;600;700&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    /* Prevent zoom on iOS when focusing inputs */
    font-size: 16px;
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
  }
  
  body {
    font-family: ${theme.fonts.main};
    background-color: ${theme.colors.background};
    color: ${theme.colors.text};
    line-height: 1.6;
    min-height: 100vh;
    /* Improve touch scrolling on iOS */
    -webkit-overflow-scrolling: touch;
    /* Prevent horizontal scroll */
    overflow-x: hidden;
    /* Support for safe areas on mobile devices */
    padding-top: ${theme.mobile.safeAreaTop};
    padding-bottom: ${theme.mobile.safeAreaBottom};
    padding-left: ${theme.mobile.safeAreaLeft};
    padding-right: ${theme.mobile.safeAreaRight};
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fonts.heading};
    font-weight: 600;
    margin-bottom: ${theme.spacing.md};
    /* Better text rendering */
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  /* Mobile-optimized headings */
  @media (max-width: ${theme.breakpoints.mobile}) {
    h1 { font-size: 1.75rem; line-height: 1.2; }
    h2 { font-size: 1.5rem; line-height: 1.3; }
    h3 { font-size: 1.25rem; line-height: 1.4; }
    h4 { font-size: 1.125rem; line-height: 1.4; }
    h5 { font-size: 1rem; line-height: 1.5; }
    h6 { font-size: 0.875rem; line-height: 1.5; }
  }
  
  button {
    cursor: pointer;
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
    border: none;
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    font-family: ${theme.fonts.main};
    font-weight: 500;
    transition: all 0.3s ease;
    /* Ensure minimum touch target size */
    min-height: ${theme.mobile.minTouchTarget};
    min-width: ${theme.mobile.minTouchTarget};
    /* Improve touch responsiveness */
    touch-action: manipulation;
    /* Remove tap highlight on mobile */
    -webkit-tap-highlight-color: transparent;
    
    &:hover {
      opacity: 0.9;
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    /* Mobile-specific button styles */
    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: ${theme.spacing.md} ${theme.spacing.lg};
      font-size: 1rem;
      border-radius: ${theme.borderRadius.large};
      
      &:hover {
        /* Disable hover effects on touch devices */
        transform: none;
        opacity: 1;
      }
      
      &:active {
        transform: scale(0.98);
        opacity: 0.8;
      }
    }
  }
  
  input, textarea, select {
    font-family: ${theme.fonts.main};
    border: 1px solid #e0e0e0;
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    width: 100%;
    /* Ensure minimum touch target size */
    min-height: ${theme.mobile.minTouchTarget};
    /* Prevent zoom on iOS */
    font-size: 16px;
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px rgba(167, 199, 231, 0.2);
    }
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: ${theme.spacing.md};
      border-radius: ${theme.borderRadius.large};
      font-size: 16px; /* Prevent zoom on iOS */
    }
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    /* Ensure minimum touch target size for links */
    min-height: ${theme.mobile.minTouchTarget};
    display: inline-flex;
    align-items: center;
    /* Remove tap highlight */
    -webkit-tap-highlight-color: transparent;
    
    &:hover {
      text-decoration: underline;
    }
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      &:hover {
        text-decoration: none;
      }
      
      &:active {
        opacity: 0.7;
      }
    }
  }
  
  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${theme.spacing.lg};
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: 0 ${theme.spacing.md};
    }
  }
  
  /* Mobile-specific utility classes */
  .mobile-only {
    display: none;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      display: block;
    }
  }
  
  .desktop-only {
    display: block;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      display: none;
    }
  }
  
  .mobile-flex {
    @media (max-width: ${theme.breakpoints.mobile}) {
      display: flex;
    }
  }
  
  .mobile-hidden {
    @media (max-width: ${theme.breakpoints.mobile}) {
      display: none;
    }
  }
  
  /* Improve scrolling performance */
  .scroll-container {
    -webkit-overflow-scrolling: touch;
    overflow-y: auto;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      /* Add momentum scrolling on iOS */
      -webkit-overflow-scrolling: touch;
    }
  }
  
  /* Mobile-friendly modal and overlay styles */
  .modal-overlay {
    @media (max-width: ${theme.breakpoints.mobile}) {
      padding: ${theme.spacing.md};
    }
  }
  
  /* Prevent text selection on UI elements */
  .no-select {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  
  /* Loading states */
  .loading {
    pointer-events: none;
    opacity: 0.7;
  }
  
  /* Focus styles for accessibility */
  .focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

export default GlobalStyles; 